import { useState, useEffect } from 'react';
import { Box, TextField, FormControlLabel, Switch, Autocomplete } from '@mui/material';
import { ICrawling, IGroup, IUser } from 'common';

interface CrawlingFormFieldsProps {
  crawling?: ICrawling | null;
  groups: IGroup[];
  users: IUser[];
  onValidItem: (crawling: Partial<ICrawling> | null) => void;
  defaultHashtag?: string;
  defaultGroup?: IGroup;
  disabled?: boolean;
}

export function CrawlingFormFields(props: CrawlingFormFieldsProps) {
  const isCreating = !props.crawling;
  const [hashtag, setHashtag] = useState(props.crawling?.hashtag || props.defaultHashtag || '');
  const [active, setActive] = useState(props.crawling?.active ?? true);
  const [dailyQuota, setDailyQuota] = useState(props.crawling?.dailyQuota || 1000);
  const [targetGroupId, setTargetGroupId] = useState((props.crawling?.group?._id || props.defaultGroup?._id || '') as string);
  const [ownerId, setOwnerId] = useState((props.crawling?.owner?._id || '') as string);

  useEffect(() => {
    // Set default owner to first user if available and in creation mode
    if (isCreating && props.users.length > 0 && !ownerId) {
      setOwnerId(props.users[0]._id || '');
    }
  }, [props.users, isCreating, ownerId]);

  const validateAndNotify = () => {
    if (hashtag.length > 0 && dailyQuota > 0 && targetGroupId.length > 0 && ownerId.length > 0) {
      const group = props.groups.find(g => g._id === targetGroupId);
      if (!group) {
        props.onValidItem(null);
        return;
      }

      const owner = props.users.find(u => u._id === ownerId);
      if (!owner) {
        props.onValidItem(null);
        return;
      }

      const crawling: Partial<ICrawling> = {
        ...props.crawling,
        hashtag,
        active,
        dailyQuota,
        group: group as any,
        owner: owner as any,
        updated_at: props.crawling?.updated_at || new Date(),
      };

      props.onValidItem(crawling);
    } else {
      props.onValidItem(null);
    }
  };

  useEffect(validateAndNotify, [hashtag, active, dailyQuota, targetGroupId, ownerId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Hashtag"
        value={hashtag}
        onChange={(e) => setHashtag(e.target.value)}
        required
        disabled={isCreating || props.disabled}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
          },
        }}
      />
      <FormControlLabel
        control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={props.disabled} />}
        label="Active"
      />
      <TextField
        type="number"
        value={dailyQuota}
        label="Daily Quota"
        required
        onChange={(e) => setDailyQuota(parseInt(e.target.value))}
        disabled={props.disabled}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
          },
        }}
      />
      <Autocomplete
        value={props.groups.find(g => g._id === targetGroupId) || null}
        options={props.groups.sort((a, b) => a.name.localeCompare(b.name))}
        getOptionLabel={(g) => g.name}
        disabled={isCreating || props.disabled}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label="Group" 
            required={true}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
              },
            }}
          />
        )}
        onChange={(_e, g) => setTargetGroupId(g?._id || '')}
      />
      <Autocomplete
        value={props.users.find(u => u._id === ownerId) || null}
        options={props.users.sort((a, b) => a.email.localeCompare(b.email))}
        getOptionLabel={(u) => u.email}
        disabled={props.disabled}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label="Owner" 
            required={true}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
              },
            }}
          />
        )}
        onChange={(_e, u) => setOwnerId(u?._id || '')}
      />
    </Box>
  );
}
